import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load token + user on first mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const stored = localStorage.getItem('user');

    if (token && stored) {
      const parsedUser = JSON.parse(stored);

      // If somehow enrollmentId is missing, clear corrupted session
      if (!parsedUser.enrollmentId && parsedUser.role === "student") {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      } else {
        setUser(parsedUser);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }
    }

    setLoading(false);
  }, []);

  // Save user data uniformly
  const saveUserSession = (token, userData) => {
    const normalizedUser = {
      id: userData.id,
      email: userData.email,
      fullName: userData.fullName,
      contact: userData.contact,
      enrollmentId: userData.enrollmentId,
      role: userData.role,
      status: userData.status
    };

    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(normalizedUser));

    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    setUser(normalizedUser);
  };

  // LOGIN
const login = async (email, password) => {
  try {
    // Normalize inputs before sending
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();

    console.log("🔐 Sending login request:", {
      email: cleanEmail,
      passwordLength: cleanPassword.length
    });

    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/auth/login`,
      {
        email: cleanEmail,
        password: cleanPassword
      }
    );

    const { token, user: userData } = response.data;

    saveUserSession(token, userData);

    return { success: true, data: userData };

  } catch (error) {
    console.error("Login error:", error.response?.data || error.message);
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "Login failed. Please try again."
    };
  }
};


  // REGISTER (JSON only – no file uploads)
  const register = async (formData) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/register`,
        formData,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const { token, user: userData } = response.data;

      saveUserSession(token, userData);

      return { success: true, data: userData };
    } catch (error) {
      console.error("Registration error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Registration failed. Please try again.",
      };
    }
  };

  // LOGOUT
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    delete axios.defaults.headers.common["Authorization"];

    setUser(null);
  };

  // UPDATE PROFILE
  const updateProfile = async (userData) => {
    try {
      const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/auth/profile`, userData);

      const updatedUser = response.data;

      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);

      return { success: true, data: updatedUser };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Update failed",
      };
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    updateProfile,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
